import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Boxes, Loader2, Check, X, AlertTriangle, AlertCircle, CircleCheck,
  HelpCircle, Package, Clock, Calculator, Calendar, RefreshCw,
  Zap, Ruler, Download, TrendingUp, ChevronDown, ChevronUp, History, PackagePlus, ClipboardCheck,
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShippySpinner } from '../components/ShippyLoader';

const WINDOW_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
];

const FORECAST_OPTIONS = [
  { label: '1 week', value: 1 },
  { label: '2 weeks', value: 2 },
  { label: '4 weeks', value: 4 },
  { label: '8 weeks', value: 8 },
];

// Light, low-chrome status styling: a thin left accent + a faint row tint,
// no glow/ring. Colour is carried by the small icon and the key numbers only,
// so a screen full of one status reads calmly instead of as a wall of colour.
const STATUS_META = {
  critical: {
    label: 'Critical',
    icon: AlertCircle,
    accent: 'bg-red-400',
    pill: 'bg-red-50 text-red-600 dark:bg-red-950/30 dark:text-red-300',
    text: 'text-red-600 dark:text-red-400',
    bar: 'bg-red-400/70',
    tint: 'bg-red-50/40 dark:bg-red-950/10',
  },
  low: {
    label: 'Low',
    icon: AlertTriangle,
    accent: 'bg-amber-400',
    pill: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
    text: 'text-amber-600 dark:text-amber-400',
    bar: 'bg-amber-400/70',
    tint: 'bg-amber-50/30 dark:bg-amber-950/10',
  },
  ok: {
    label: 'Healthy',
    icon: CircleCheck,
    accent: 'bg-emerald-400',
    pill: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
    text: 'text-emerald-600 dark:text-emerald-400',
    bar: 'bg-emerald-400/70',
    tint: '',
  },
  unknown: {
    label: 'No data',
    icon: HelpCircle,
    accent: 'bg-muted-foreground/30',
    pill: 'bg-muted text-muted-foreground',
    text: 'text-muted-foreground',
    bar: 'bg-muted-foreground/25',
    tint: '',
  },
};

const Inventory = () => {
  const [days, setDays] = useState(30);
  const [forecastWeeks, setForecastWeeks] = useState(4);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editMode, setEditMode] = useState('count');
  const [sort, setSort] = useState({ key: 'days_left', dir: 'asc' });
  const [historyId, setHistoryId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`api/inventory/boxes/?days=${days}&forecast_weeks=${forecastWeeks}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [days, forecastWeeks]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const res = await axiosInstance.get(
        `api/inventory/export/?weeks=${forecastWeeks}&days=${days}`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `box_reorder_${forecastWeeks}w.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to export reorder sheet');
    } finally {
      setExporting(false);
    }
  }, [forecastWeeks, days]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const startEdit = (box, mode) => {
    setEditingId(box.id);
    setEditMode(mode);
    setEditValue(mode === 'count' ? String(box.estimated_stock ?? 0) : '');
    setEditNote('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditNote('');
  };

  const saveEdit = async (box) => {
    const parsed = parseInt(editValue, 10);
    if (Number.isNaN(parsed) || parsed < 0 || (editMode === 'receive' && parsed === 0)) {
      setError(editMode === 'count' ? 'Count must be a non-negative integer' : 'Received must be a positive integer');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await axiosInstance.patch(`api/inventory/boxes/${box.id}/`, {
        ...(editMode === 'count' ? { stock_quantity: parsed } : { receive: parsed }),
        note: editNote,
        days,
        forecast_weeks: forecastWeeks,
      });
      setData((prev) => {
        if (!prev) return prev;
        const boxes = prev.boxes.map((b) => (b.id === box.id ? res.data : b));
        boxes.sort((a, b) => {
          if (a.days_left === null && b.days_left === null) return 0;
          if (a.days_left === null) return 1;
          if (b.days_left === null) return -1;
          return a.days_left - b.days_left;
        });
        return {
          ...prev,
          boxes,
          summary: {
            ...prev.summary,
            total_stock: boxes.reduce((sum, bx) => sum + (bx.estimated_stock ?? bx.stock_quantity ?? 0), 0),
            critical: boxes.filter((bx) => bx.status === 'critical').length,
            low: boxes.filter((bx) => bx.status === 'low').length,
            ok: boxes.filter((bx) => bx.status === 'ok').length,
            unknown: boxes.filter((bx) => bx.status === 'unknown').length,
          },
        };
      });
      cancelEdit();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to update stock');
    } finally {
      setSaving(false);
    }
  };

  const summary = data?.summary;
  const allBoxes = data?.boxes || [];

  // Live aggregates: estimated stock total, next-out box, days since oldest count
  const aggregates = useMemo(() => {
    const totalEstimated = allBoxes.reduce(
      (s, b) => s + (b.estimated_stock ?? b.stock_quantity ?? 0), 0
    );
    const totalCounted = allBoxes.reduce((s, b) => s + (b.stock_quantity ?? 0), 0);
    const totalUsed = allBoxes.reduce((s, b) => s + (b.used_since_count ?? 0), 0);
    const totalReserved = allBoxes.reduce((s, b) => s + (b.reserved ?? 0), 0);

    // Next box to run out (smallest days_left > 0, or 'Out' boxes if any)
    const outBoxes = allBoxes.filter((b) => b.days_left !== null && b.days_left <= 0);
    const sortedByDaysLeft = allBoxes
      .filter((b) => b.days_left !== null && b.days_left > 0)
      .sort((a, b) => a.days_left - b.days_left);
    const nextOut = outBoxes[0] || sortedByDaysLeft[0] || null;

    // Stalest count (oldest stock_updated_at)
    let oldestCount = null;
    let oldestCountAge = 0;
    allBoxes.forEach((b) => {
      if (!b.stock_updated_at) return;
      const ageDays = (Date.now() - new Date(b.stock_updated_at).getTime()) / (1000 * 60 * 60 * 24);
      if (ageDays > oldestCountAge) {
        oldestCountAge = ageDays;
        oldestCount = b;
      }
    });

    return { totalEstimated, totalCounted, totalUsed, totalReserved, nextOut, oldestCount, oldestCountAge };
  }, [allBoxes]);

  const boxes = useMemo(() => {
    let list = allBoxes;
    if (statusFilter !== 'all') list = list.filter((b) => b.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((b) =>
        b.name.toLowerCase().includes(q) || (b.bin_code || '').toLowerCase().includes(q)
      );
    }
    const value = (b) => (sort.key === 'bin_code' ? b.bin_code : b[sort.key]);
    return [...list].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [allBoxes, statusFilter, search, sort]);

  const toggleSort = (key) => {
    setSort((cur) => (cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const formatDaysLeft = (d) => {
    if (d === null || d === undefined) return '—';
    if (d <= 0) return 'Out';
    if (d < 1) return '< 1d';
    return `${d.toFixed(1)}d`;
  };

  const formatRelative = (iso) => {
    if (!iso) return 'Never counted';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const hours = diff / (1000 * 60 * 60);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${Math.round(hours)}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  };

  const formatStockoutDate = (daysLeft) => {
    if (daysLeft === null || daysLeft === undefined) return null;
    const d = new Date(Date.now() + Math.max(0, daysLeft) * 24 * 60 * 60 * 1000);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="px-6 py-8 space-y-6 max-w-[1500px] mx-auto">
      {/* HERO HEADER */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-card via-card to-primary/5 p-6 md:p-8">
        <div className="absolute inset-y-0 right-0 w-1/2 opacity-[0.08] pointer-events-none">
          <Boxes className="h-full w-full" strokeWidth={0.5} />
        </div>
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20 shadow-inner">
              <Boxes className="h-7 w-7" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight">Box Inventory</h1>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Last count plus deliveries, minus the boxes on Odoo OUTs set to done. Open orders reserve theirs.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 h-9 text-xs">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Usage</span>
              <select
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10))}
                className="bg-transparent text-foreground font-medium focus:outline-none cursor-pointer"
              >
                {WINDOW_OPTIONS.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 h-9 text-xs">
              <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Forecast</span>
              <select
                value={forecastWeeks}
                onChange={(e) => setForecastWeeks(parseInt(e.target.value, 10))}
                className="bg-transparent text-foreground font-medium focus:outline-none cursor-pointer"
              >
                {FORECAST_OPTIONS.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
            <Button onClick={fetchInventory} variant="outline" size="sm" disabled={loading}>
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Refresh
            </Button>
            <Button onClick={handleExport} variant="outline" size="sm" disabled={exporting || loading}>
              {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
              Export
            </Button>
          </div>
        </div>

        {/* HERO METRICS STRIP */}
        {summary && (
          <div className="relative mt-6 grid grid-cols-2 lg:grid-cols-5 gap-3">
            <HeroMetric
              icon={Package}
              label="Box types tracked"
              value={summary.total_boxes}
              accent="default"
            />
            <HeroMetric
              icon={Boxes}
              label="In stock"
              value={aggregates.totalEstimated.toLocaleString()}
              sub={`${aggregates.totalReserved.toLocaleString()} reserved by open orders`}
              accent="primary"
            />
            <HeroMetric
              icon={AlertCircle}
              label="Need attention"
              value={summary.critical + summary.low}
              sub={`${summary.critical} critical · ${summary.low} low`}
              accent={summary.critical > 0 ? 'red' : summary.low > 0 ? 'amber' : 'emerald'}
            />
            <HeroMetric
              icon={Clock}
              label="Next stockout"
              value={
                aggregates.nextOut
                  ? aggregates.nextOut.days_left <= 0
                    ? 'Now'
                    : formatStockoutDate(aggregates.nextOut.days_left)
                  : '—'
              }
              sub={
                aggregates.nextOut
                  ? `${aggregates.nextOut.name} (${aggregates.nextOut.bin_code})`
                  : 'Nothing flagged'
              }
              accent={
                aggregates.nextOut && aggregates.nextOut.days_left <= 7 ? 'red' : 'amber'
              }
            />
            <HeroMetric
              icon={TrendingUp}
              label={`Forecast · next ${forecastWeeks}w`}
              value={(summary.forecast_units_total ?? 0).toLocaleString()}
              sub={
                data?.seasonal_factors?.length
                  ? `boxes needed · season ×${(
                      data.seasonal_factors.reduce((a, b) => a + b, 0) /
                      data.seasonal_factors.length
                    ).toFixed(2)} vs last yr`
                  : 'boxes needed (run-rate)'
              }
              accent="primary"
            />
          </div>
        )}
      </div>

      {/* CRITICAL ALERT */}
      {summary && summary.critical > 0 && (
        <div className="rounded-xl border border-red-500/40 bg-red-50/70 dark:bg-red-950/15 p-4 flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-600 dark:text-red-400">
            <Zap className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">
              {summary.critical} {summary.critical === 1 ? 'box' : 'boxes'} need immediate attention
            </p>
            <p className="text-xs text-red-700/80 dark:text-red-300/80 mt-0.5">
              {allBoxes
                .filter((b) => b.status === 'critical')
                .slice(0, 4)
                .map((b) => `${b.name} (${formatDaysLeft(b.days_left)})`)
                .join(' · ')}
              {summary.critical > 4 && ` · +${summary.critical - 4} more`}
            </p>
          </div>
        </div>
      )}

      {/* SYNC HEALTH */}
      {data?.sync?.stale && (
        <div className="rounded-xl border border-red-500/30 bg-red-50/50 dark:bg-red-950/10 p-3 flex items-center gap-2 text-xs">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0" />
          <span className="text-red-800 dark:text-red-300">
            The sync from Odoo is not up to date
            {data.sync.last_run_at ? ` (last run ${formatRelative(data.sync.last_run_at)})` : ' (it has not run yet)'}
            , so recent OUTs may be missing from these numbers.
            {data.sync.last_error ? ` Error: ${data.sync.last_error}` : ''}
          </span>
        </div>
      )}

      {/* STALE COUNT NUDGE */}
      {aggregates.oldestCount && aggregates.oldestCountAge > 14 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/10 p-3 flex items-center gap-2 text-xs">
          <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span className="text-amber-800 dark:text-amber-300">
            Oldest count is <strong>{Math.round(aggregates.oldestCountAge)} days old</strong> ({aggregates.oldestCount.name}).
            Recount soon: errors add up the longer you wait.
          </span>
        </div>
      )}

      {/* FILTER BAR */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-border/70 surface-panel p-1">
          {[
            { key: 'all', label: 'All', count: allBoxes.length },
            { key: 'critical', label: 'Critical', count: summary?.critical || 0 },
            { key: 'low', label: 'Low', count: summary?.low || 0 },
            { key: 'ok', label: 'Healthy', count: summary?.ok || 0 },
            { key: 'unknown', label: 'No data', count: summary?.unknown || 0 },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                statusFilter === f.key
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {f.label}
              <span className={`text-[10px] rounded-full px-1.5 ${
                statusFilter === f.key ? 'bg-primary-foreground/20' : 'bg-muted'
              }`}>{f.count}</span>
            </button>
          ))}
        </div>
        <div className="flex-1 max-w-sm">
          <Input
            placeholder="Search box name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* BOX TABLE */}
      {loading && !data ? (
        <div className="p-12 flex justify-center"><ShippySpinner /></div>
      ) : (
        <div className="rounded-lg border border-border/60 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.key} className={`px-3 py-2 font-medium whitespace-nowrap ${c.left ? 'text-left' : 'text-right'}`} title={c.title}>
                    <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-0.5 hover:text-foreground">
                      {c.label}
                      {sort.key === c.key && (sort.dir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
                    </button>
                  </th>
                ))}
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {boxes.map((box) => (
                <BoxRow
                  key={box.id}
                  box={box}
                  isEditing={editingId === box.id}
                  editMode={editMode}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  editNote={editNote}
                  setEditNote={setEditNote}
                  saving={saving}
                  onStartEdit={(mode) => startEdit(box, mode)}
                  onSaveEdit={() => saveEdit(box)}
                  onCancelEdit={cancelEdit}
                  showHistory={historyId === box.id}
                  onToggleHistory={() => setHistoryId(historyId === box.id ? null : box.id)}
                  formatDaysLeft={formatDaysLeft}
                  formatRelative={formatRelative}
                />
              ))}
              {boxes.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="p-8 text-center text-muted-foreground">
                    No boxes match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center pt-4">
        Odoo does not record which box was used, so each OUT takes the boxes Shippy planned for it.
        Available is in stock minus what open orders reserve. Forecast scales recent order demand by last
        year's pattern for the same weeks. Pallets and direct shipments excluded. A count restarts the deductions.
      </p>
    </div>
  );
};

// ---------------- HERO METRIC ----------------
const HeroMetric = ({ icon: Icon, label, value, sub, accent = 'default' }) => {
  const accents = {
    default: 'text-foreground bg-muted/40',
    primary: 'text-primary bg-primary/10',
    red: 'text-red-600 dark:text-red-400 bg-red-500/10',
    amber: 'text-amber-600 dark:text-amber-400 bg-amber-500/10',
    emerald: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10',
  };
  return (
    <div className="rounded-xl border border-border bg-background/70 backdrop-blur p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <div className={`h-7 w-7 inline-flex items-center justify-center rounded-md ${accents[accent]}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <p className="text-2xl font-bold mt-1.5 leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-1 truncate" title={sub}>{sub}</p>}
    </div>
  );
};

// ---------------- BOX TABLE ----------------
const COLUMNS = [
  { key: 'bin_code', label: 'Box', left: true },
  { key: 'estimated_stock', label: 'In stock', title: 'Last count + deliveries - boxes on OUTs done since that count' },
  { key: 'reserved', label: 'Reserved', title: 'Planned for orders whose OUT is still open' },
  { key: 'available_stock', label: 'Available', title: 'In stock - reserved' },
  { key: 'per_day_avg', label: 'Per day', title: 'Boxes planned per day, over the usage window' },
  { key: 'forecast_units', label: 'Forecast', title: "Run-rate x last year's seasonal pattern, over the forecast window" },
  { key: 'days_left', label: 'Days left', title: 'Available / per day' },
  { key: 'weeks_of_cover', label: 'Cover', title: 'Weeks of available stock, seasonally adjusted' },
  { key: 'stock_updated_at', label: 'Counted', title: 'Last physical count' },
];

const BoxHistory = ({ boxId, formatRelative }) => {
  const [history, setHistory] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    axiosInstance.get(`api/inventory/boxes/${boxId}/history/?limit=15`)
      .then((res) => setHistory(res.data))
      .catch(() => setFailed(true));
  }, [boxId]);

  if (failed) return <p className="text-xs text-red-600">Could not load the history.</p>;
  if (!history) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  const REASONS = { manual: 'Edit', receive: 'Received', adjust: 'Count', initial: 'Initial', consume: 'Used' };
  return (
    <div className="grid md:grid-cols-2 gap-4 text-xs">
      <div>
        <p className="font-medium mb-1">Counts and deliveries</p>
        {history.movements.length === 0 && <p className="text-muted-foreground">None yet.</p>}
        {history.movements.map((m) => (
          <p key={m.id} className="text-muted-foreground">
            {formatRelative(m.created_at)}: {REASONS[m.reason] || m.reason} {m.delta > 0 ? '+' : ''}{m.delta}, now {m.new_quantity}
            {m.user ? ` (${m.user})` : ''}{m.note ? `: ${m.note}` : ''}
          </p>
        ))}
      </div>
      <div>
        <p className="font-medium mb-1">Recent OUTs</p>
        {history.shipments.length === 0 && <p className="text-muted-foreground">None yet.</p>}
        {history.shipments.map((r) => (
          <p key={r.picking} className="text-muted-foreground">
            {formatRelative(r.done_at)}: {r.picking} {r.order} took {r.quantity}
            {r.source === 'failed' ? ' (could not pack this OUT, used the order plan)' : ''}
          </p>
        ))}
      </div>
    </div>
  );
};

const BoxRow = ({
  box, isEditing, editMode, editValue, setEditValue, editNote, setEditNote, saving,
  onStartEdit, onSaveEdit, onCancelEdit, showHistory, onToggleHistory, formatDaysLeft, formatRelative,
}) => {
  const meta = STATUS_META[box.status] || STATUS_META.unknown;
  const StatusIcon = meta.icon;
  const cell = 'px-3 py-2 text-right tabular-nums whitespace-nowrap';

  return (
    <>
      <tr className={`border-t border-border/50 ${meta.tint}`}>
        <td className="px-3 py-2 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <StatusIcon className={`h-3.5 w-3.5 shrink-0 ${meta.text}`} title={meta.label} />
            <div className="leading-tight">
              <div className="font-medium">{box.name}</div>
              <div className="text-[10px] font-mono text-muted-foreground">{box.bin_code}</div>
            </div>
          </div>
        </td>
        <td className={cell}>
          {(box.estimated_stock ?? 0).toLocaleString()}
          {box.stock_balance < 0 && (
            <div className="text-[10px] text-red-600 dark:text-red-400" title="More boxes left than were counted: recount, or enter the missing delivery">
              {box.stock_balance.toLocaleString()}, recount
            </div>
          )}
        </td>
        <td className={`${cell} text-muted-foreground`}>{(box.reserved ?? 0).toLocaleString()}</td>
        <td className={`${cell} font-semibold ${meta.text}`}>{(box.available_stock ?? 0).toLocaleString()}</td>
        <td className={`${cell} text-muted-foreground`}>{(box.per_day_avg || 0).toFixed(1)}</td>
        <td className={`${cell} text-muted-foreground`}>{box.forecast_units != null ? box.forecast_units.toLocaleString() : '—'}</td>
        <td className={`${cell} ${meta.text}`}>{formatDaysLeft(box.days_left)}</td>
        <td className={`${cell} text-muted-foreground`}>{box.weeks_of_cover != null ? `${box.weeks_of_cover}w` : '—'}</td>
        <td className={`${cell} text-muted-foreground`}>{box.stock_updated_at ? formatRelative(box.stock_updated_at) : 'Never'}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {isEditing ? (
            <div className="flex items-center justify-end gap-1">
              <Input
                type="number"
                min="0"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="h-7 w-20 text-right text-xs"
                autoFocus
                placeholder={editMode === 'count' ? 'Counted' : 'Received'}
              />
              <Input
                type="text"
                placeholder={editMode === 'count' ? 'note' : 'PO / note'}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                className="h-7 w-24 text-xs"
              />
              <button
                onClick={onSaveEdit}
                disabled={saving}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                title={editMode === 'count' ? 'Save count' : 'Add delivery'}
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              </button>
              <button
                onClick={onCancelEdit}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-muted hover:bg-muted/80"
                title="Cancel"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-1">
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => onStartEdit('count')} title="Enter a physical count: deductions restart from here">
                <ClipboardCheck className="h-3 w-3 mr-1" /> Count
              </Button>
              <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => onStartEdit('receive')} title="Add a delivery to the stock">
                <PackagePlus className="h-3 w-3 mr-1" /> Received
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onToggleHistory} title="History">
                <History className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </td>
      </tr>
      {showHistory && (
        <tr className="border-t border-border/30 bg-muted/20">
          <td colSpan={COLUMNS.length + 1} className="px-4 py-3">
            <BoxHistory boxId={box.id} formatRelative={formatRelative} />
          </td>
        </tr>
      )}
    </>
  );
};

export default Inventory;
